/**
 * The bottom of a scoped agent chat: the error banner, the thread's queued
 * messages (typed while a turn ran; sent one at a time once it is free, see
 * `useAgentChat`), the composer (with mentions, mode select and optional slash
 * commands) and the disclaimer.
 */
import { useMemo, type FC, type ReactNode } from "react";

import { PromptInputProvider } from "@/components/ai-elements/prompt-input";
import { Button } from "@/components/ui/button";
import {
  Composer,
  type SlashCommand
} from "@/features/caseIde/chat/components/Composer";
import { QuestionCard } from "@/features/caseIde/chat/components/QuestionCard";
import type { FirmSkillSummary } from "@/features/firmKnowledge/types";
import type { ResearchSourceId } from "@/features/research";
import { cn } from "@/lib/utils";

import {
  ApprovalNeededPill,
  PausedNote
} from "@/features/caseIde/chat/components/WaitingOnYou";
import type { AttachmentFilePart } from "../attachments/types";
import { EnableAlertsPrompt } from "../../agentAttention/components/EnableAlertsPrompt";

import { useOnScreenChip } from "../onScreen/useOnScreenChip";
import {
  PENDING_QUESTION_PLACEHOLDER,
  pendingApprovalPart,
  pendingAskUserPart
} from "../useAgentChat";
import { useAgentSpecs } from "../useAgentSpecs";
import type { ScopedAgentChat } from "../useScopedAgentChat";
import { PreferencesChip } from "./PreferencesChip";
import { QueuedMessages } from "./QueuedMessages";

export const DISCLAIMER = "Casey can make mistakes. Please verify responses.";

type Props = {
  scoped: ScopedAgentChat;
  className?: string;
  /** Rendered above the composer (e.g. starter chips in the panel). */
  above?: ReactNode;
  /** `/` commands offered by the workspace composer. */
  slashCommands?: SlashCommand[];
  placeholder?: string;
  autoFocus?: boolean;
  /** Hide the disclaimer. */
  hideDisclaimer?: boolean;
  /** Lets a host push a whole prompt into the composer (the Requests IDE). */
  onPromptRequest?: (register: (text: string) => void) => () => void;
  /** Firm skills: the chip pins one on the thread, Play runs it. */
  skills?: FirmSkillSummary[];
  onRunSkill?: (skill: FirmSkillSummary) => void;
  /**
   * Dock a pending `ask_user` question on the composer, as the Case IDE panel
   * does. On by default: the transcript keeps only answered questions, so a
   * surface that turns this off must render the open question itself.
   */
  dockQuestion?: boolean;
  /** The live turn line, just above the box. */
  status?: ReactNode;
  /**
   * Show the record open on the page as a removable chip (the global panel,
   * whose turns send it). Workspaces that send their own selection leave it off.
   */
  showOnScreen?: boolean;
  /**
   * Asked before a message is sent; `false` holds it (the panel asks whether
   * it belongs in a new chat and sends it itself).
   */
  onBeforeSend?: (text: string, files?: AttachmentFilePart[]) => boolean;
  /**
   * Offer the research magnifier (library sources for the next message). Off
   * for agents without the library tools (onboarding, the skill builder).
   */
  research?: boolean;
  /**
   * The magnifier's picks held by the host instead (the library chat): they
   * persist across sends, a send waits for at least one, and they ride on the surface's own
   * `messageMetadata` rather than this send's.
   */
  researchPicker?: {
    value: ResearchSourceId[];
    onChange: (ids: ResearchSourceId[]) => void;
  };
  /** Show the model and speed picker. The library chat leaves it off. */
  showModelSelect?: boolean;
  /**
   * Show Agent / Plan. Defaults to whether this agent can plan. The research
   * chat leaves it off.
   */
  showModeSelect?: boolean;
  /** Unsent text key. Defaults to this scope and thread. */
  draftKey?: string;
};

export const AgentComposerBar: FC<Props> = ({
  scoped,
  className,
  above,
  slashCommands,
  placeholder,
  autoFocus,
  hideDisclaimer,
  onPromptRequest,
  skills,
  onRunSkill,
  dockQuestion = true,
  status,
  showOnScreen = false,
  onBeforeSend,
  research = true,
  researchPicker,
  showModelSelect = true,
  showModeSelect,
  draftKey
}) => {
  const {
    chat,
    me,
    teamMembers,
    label,
    supportsPlanMode,
    send,
    onAnswer,
    researchSources,
    setResearchSources,
    takeResearchMetadata
  } = scoped;
  const contextChip = useOnScreenChip(showOnScreen);
  const { queue } = chat;
  const { models, autoModes } = useAgentSpecs();
  const takeMetadata = researchPicker ? () => undefined : takeResearchMetadata;
  const pendingQuestion = useMemo(
    () =>
      dockQuestion && !chat.restoring
        ? pendingAskUserPart(chat.messages)
        : null,
    [dockQuestion, chat.restoring, chat.messages]
  );
  const pendingApprovalId = useMemo(() => {
    if (chat.restoring) return null;
    const part = pendingApprovalPart(chat.messages);
    return part && "approval" in part ? part.approval?.id ?? null : null;
  }, [chat.restoring, chat.messages]);

  return (
    <div className={cn("shrink-0 space-y-2", className)}>
      {chat.error ? (
        <div
          className="flex items-center justify-between gap-2 rounded-sm border border-destructive/40 bg-destructive/5 px-2 py-1 text-xs text-destructive"
          data-testid="agent-chat-error"
        >
          <span className="truncate">{chat.error}</span>
          <div className="flex shrink-0 gap-1">
            <Button
              variant="ghost"
              size="sm"
              className="h-6 px-2 text-xs"
              onClick={() => void chat.regenerate()}
            >
              Retry
            </Button>
            <Button
              variant="ghost"
              size="sm"
              className="h-6 px-2 text-xs"
              onClick={chat.clearError}
            >
              Dismiss
            </Button>
          </div>
        </div>
      ) : null}
      {above}
      <EnableAlertsPrompt />
      {pendingApprovalId ? (
        <ApprovalNeededPill approvalId={pendingApprovalId} />
      ) : null}
      {status}
      {/*
        Queued rows tuck under the box; an open question is a tab on the box
        itself, so the queue sits above it with its own margin.
      */}
      <div>
        <QueuedMessages
          items={queue.items}
          steering={chat.steerInbox}
          paused={queue.paused}
          meUid={me.uid || null}
          onEdit={(id, text) => void queue.edit(id, text)}
          onRemove={(id) => void queue.remove(id)}
          onReorder={(ids) => void queue.reorder(ids)}
          onSendNow={(id) => void queue.sendNow(id)}
          onResume={queue.resume}
          className={pendingQuestion ? "mb-2 pb-1" : undefined}
        />
        {pendingQuestion ? (
          <div
            className="max-h-[50vh] overflow-y-auto"
            data-testid="docked-question"
          >
            <QuestionCard docked part={pendingQuestion} onAnswer={onAnswer} />
          </div>
        ) : null}
        {pendingApprovalId ? <PausedNote className="pb-1" /> : null}
        <PromptInputProvider>
          <Composer
            status={chat.status}
            disabled={chat.threadsLoading || chat.restoring}
            onSend={(text, files) => {
              // A held message keeps the picks; the host sends it with them.
              if (onBeforeSend && !onBeforeSend(text, files)) return;
              send(text, files, takeMetadata());
            }}
            contextChip={contextChip}
            onStop={() => void chat.stopTurn()}
            onQueue={(text, files) => void queue.enqueue(text, files)}
            onSendNow={(text, files) =>
              void chat.sendNow(text, takeMetadata(), files)
            }
            attachmentScope={chat.scope}
            draftKey={
              draftKey ??
              `agent:${chat.scope.kind}:${chat.scope.id}:${
                chat.threadId ?? "new"
              }`
            }
            mode={chat.mode}
            onModeChange={(m) => void chat.setMode(m)}
            showModeSelect={showModeSelect ?? supportsPlanMode}
            models={showModelSelect ? models : undefined}
            autoModes={autoModes}
            model={chat.model}
            thinking={chat.thinking}
            optimizeFor={chat.optimizeFor}
            onModelChange={chat.setModel}
            onThinkingChange={chat.setThinking}
            onOptimizeForChange={chat.setOptimizeFor}
            agentLabel={label}
            teamMembers={teamMembers}
            meUid={me.uid || null}
            activeTurn={chat.activeTurn}
            followUp={chat.messages.length > 0}
            slashCommands={slashCommands}
            placeholder={
              pendingQuestion ? PENDING_QUESTION_PLACEHOLDER : placeholder
            }
            skills={skills}
            skillId={chat.skillId}
            onSkillChange={skills ? chat.setSkill : undefined}
            skillPinned={chat.skillPinned}
            onSkillPinChange={chat.setSkillPinned}
            researchSources={
              researchPicker?.value ?? (research ? researchSources : undefined)
            }
            onResearchSourcesChange={
              researchPicker?.onChange ??
              (research ? setResearchSources : undefined)
            }
            researchSourcesRequired={Boolean(researchPicker)}
            preferencesControl={
              <PreferencesChip
                scope={chat.scope}
                threadId={chat.threadId}
                refreshKey={chat.status === "ready"}
              />
            }
            onRunSkill={onRunSkill}
            autoFocus={autoFocus}
            onPromptRequest={onPromptRequest}
          />
        </PromptInputProvider>
      </div>
      {hideDisclaimer ? null : (
        <p
          className="text-center text-xs text-muted-foreground"
          data-testid="agent-panel-disclaimer"
        >
          {DISCLAIMER}
        </p>
      )}
    </div>
  );
};
